import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Typography, IconButton, Grid, Box } from '@mui/material'
import React, { useEffect } from 'react'
import { useFormState, useWatch } from 'react-hook-form'
import { useHideDirtyState } from 'src/Libs/flow/advanced-settings/providers'
import { DirtyMark, TrashIcon } from 'src/Libs/flow/icons'
import { InstallationFormModel } from 'src/Libs/flow/installation/forms'
import { useInstallationForm } from 'src/Libs/flow/installation/hooks'
import { checkTrueRecursively } from 'src/Libs/flow/utils'
import { makeStyles } from 'tss-react/mui'
import { ScheduleType } from '../../types'
import { NumberField, TimePicker } from 'src/Libs/inputs/react-hook-form'
import { WeekdaySelector } from './WeekdaySelector'
import { SettingsBox } from 'src/Elements/Data'
import { ErrorMessage } from '@e3dc-react/shell/Elements/Error'
import { useInstallationAccessRightsContext } from 'src/Libs/installation-rights-management/provider/InstallationAccessRightsContext'

const useStyles = makeStyles()({
  titleWeekdayContainer: {
    alignItems: 'start',
    justifyContent: 'space-between',
  },
  titleIconContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
})

interface ChargingWeeklyScheduleInputProps {
  evseType: keyof InstallationFormModel['chargingStations']
  evseIndex: number
  scheduleType: ScheduleType
  calendarIndex: number
  remove: () => void
  weekdayIndexes: {
    monday: number | null
    tuesday: number | null
    wednesday: number | null
    thursday: number | null
    friday: number | null
    saturday: number | null
    sunday: number | null
  }
  disableDeleteButton: boolean
}

/** A list of inputs for a single schedule in the weekly schedule, with optional inputs for departure time and energy wanted */
export const ChargingWeeklyScheduleInput: React.FC<ChargingWeeklyScheduleInputProps> = ({
  evseType,
  evseIndex,
  scheduleType,
  calendarIndex,
  remove,
  weekdayIndexes,
  disableDeleteButton,
}) => {
  const { t } = useCustomTranslation()
  const { hasInstallationWritingRights: canEdit } = useInstallationAccessRightsContext()
  const { classes } = useStyles()

  const hideDirtyState = useHideDirtyState()

  const { control, setError, clearErrors } = useInstallationForm()
  const { dirtyFields, errors } = useFormState({
    control,
    name: `chargingStations.${evseType}.${evseIndex}.${scheduleType}.${calendarIndex}`,
  })

  const isDirty = checkTrueRecursively(dirtyFields.chargingStations?.[evseType]?.[evseIndex]?.[scheduleType]?.[calendarIndex])
  const isError = !!Object.keys(errors.chargingStations?.[evseType]?.[evseIndex]?.[scheduleType]?.[calendarIndex] ?? {}).length

  const isEfficientCalendar = scheduleType === 'efficientPvSchedule'

  const weekdaysFormKey = `chargingStations.${evseType}.${evseIndex}.${scheduleType}.${calendarIndex}.weekDays` as const

  const weekdays = useWatch({
    control,
    name: weekdaysFormKey,
  })

  const noWeekdaySelected = Object.values(weekdays).every(day => day === false)

  useEffect(() => {
    if (noWeekdaySelected) {
      setError(weekdaysFormKey, { type: 'charging-schedule-error' })
    } else {
      clearErrors(weekdaysFormKey)
    }
  }, [weekdaysFormKey, noWeekdaySelected, setError, clearErrors])

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} container className={classes.titleWeekdayContainer}>
        {/* Title & Icon */}
        <Grid item xs={12} sm={6}>
          <Grid container className={classes.titleIconContainer}>
            <Grid item>
              <Typography variant="h4" sx={{ color: isError ? theme => theme.palette.error.main : undefined }}>
                {t('installation.evseEditor.chargingTargetRule')} {calendarIndex + 1}
                {isDirty && !hideDirtyState && <DirtyMark />}
              </Typography>
            </Grid>
            <Grid item>
              <IconButton size="large" onClick={remove} sx={{ visibility: canEdit ? undefined : 'hidden' }} disabled={disableDeleteButton}>
                <TrashIcon />
              </IconButton>
            </Grid>
          </Grid>
        </Grid>

        {/* Weekdays */}
        <Grid item xs={12} sm={6}>
          <Box sx={{ minWidth: { xs: '100%', sm: 'auto' } }}>
            <WeekdaySelector calendarIndex={calendarIndex} weekdayIndexes={weekdayIndexes} name={weekdaysFormKey} />
          </Box>
        </Grid>
      </Grid>

      {noWeekdaySelected && (
        <Grid item xs={12} sm={6}>
          <ErrorMessage title={t('installation.tariff.missingDay')} />
        </Grid>
      )}

      {/* Departure Time */}
      {isEfficientCalendar && (
        <Grid item xs={12}>
          <SettingsBox
            title={t('installation.evseEditor.departureTime')}
            suffix={
              <TimePicker
                name={`chargingStations.${evseType}.${evseIndex}.${scheduleType}.${calendarIndex}.departureTime`}
                onTransform={value => (value ? value.toISOString() : null)}
                disabled={!canEdit}
              />
            }
          />
        </Grid>
      )}

      {/* Energy */}
      <Grid item xs={12}>
        <SettingsBox
          title={t('installation.evseEditor.energy')}
          suffix={
            <NumberField
              label={t('installation.evseEditor.energy')}
              suffix={t('affixes.kilowattHour')}
              name={`chargingStations.${evseType}.${evseIndex}.${scheduleType}.${calendarIndex}.energyWanted`}
              min={0}
              max={300}
              precision={1}
              step={0.1}
              isDecimal
              disabled={!canEdit}
            />
          }
        />
      </Grid>
    </Grid>
  )
}
